import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  CloudSync,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import ProjectSpaces from './ProjectSpaces';
import FolderTree from './FolderTree';
import FileList from './FileList';
import FolderContentView from './FolderContentView';
import FileUpload from './FileUpload';
import FileViewer from './FileViewer';
import ProjectConnectors from './ProjectConnectors';
import PublicShareDialog from './PublicShareDialog';
import PublicShareListDialog from './PublicShareListDialog';
import { filesApi, projectSpacesApi } from '../../services/fileShareApi';

// Sidebar with collapse functionality
const SidebarWithCollapse = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        flexShrink: 0,
        // Reserve space for the toggle when collapsed so it does not cover content
        width: collapsed ? 28 : 280,
        transition: 'width 0.3s ease',
      }}
    >
      <Box
        sx={{
          width: collapsed ? 0 : 280,
          borderRight: collapsed ? 0 : 1,
          borderColor: 'divider',
          height: '100%',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          overflow: collapsed ? 'hidden' : 'auto',
          transition: 'width 0.3s ease',
        }}
      >
        {children}
      </Box>
      <IconButton
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? 'Expand folders' : 'Collapse folders'}
        sx={{
          position: 'absolute',
          top: 8,
          // Sit on the divider / in reserved strip — not over the content cards
          right: collapsed ? 2 : -12,
          zIndex: 2,
          width: 24,
          height: 24,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          boxShadow: 1,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        size="small"
      >
        {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
      </IconButton>
    </Box>
  );
};

const FileShare = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: projectIdParam, fileId: fileIdParam } = useParams();
  // Storage-only files have no DB id, so their viewer is tracked with ?preview=1
  const previewParam = new URLSearchParams(location.search).get('preview') === '1';

  const [currentView, setCurrentView] = useState('projects'); // 'projects', 'files', 'viewer', 'connectors'
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [folderPath, setFolderPath] = useState([]); // Array of folder objects for breadcrumb navigation
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [syncingBucket, setSyncingBucket] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareListDialogOpen, setShareListDialogOpen] = useState(false);
  const [selectedFileForShare, setSelectedFileForShare] = useState(null);
  const [routeLoading, setRouteLoading] = useState(Boolean(projectIdParam));
  const [routeError, setRouteError] = useState(null);

  // A viewer without a file would render an empty page, so fall back to the file list
  const isViewerActive = currentView === 'viewer' && Boolean(selectedFile);
  const isFilesActive = currentView === 'files' || (currentView === 'viewer' && !selectedFile);

  // Keep UI in sync with URL so browser Back/Forward works
  useEffect(() => {
    let cancelled = false;

    const syncFromUrl = async () => {
      setRouteError(null);

      if (!projectIdParam) {
        setSelectedProject(null);
        setSelectedFolder(null);
        setSelectedFile(null);
        setFolderPath([]);
        setCurrentView('projects');
        setRouteLoading(false);
        return;
      }

      setRouteLoading(true);
      try {
        const response = await projectSpacesApi.getById(projectIdParam);
        if (cancelled) return;

        const project = response.data || {};
        const normalizedProject = {
          ...project,
          projectId: project.projectId || project.id || projectIdParam,
          projectName: project.projectName || project.name || 'Project',
        };
        setSelectedProject(normalizedProject);

        if (fileIdParam) {
          try {
            const fileResponse = await filesApi.getById(fileIdParam);
            if (cancelled) return;
            const file = fileResponse.data || {};
            setSelectedFile({
              ...file,
              fileId: file.fileId || file.id || fileIdParam,
              fileName: file.fileName || file.name,
              fileType: file.fileType || file.type,
            });
            setCurrentView('viewer');
          } catch (fileErr) {
            console.error('Failed to load file from URL:', fileErr);
            if (!cancelled) {
              setSelectedFile(null);
              setCurrentView('files');
              navigate(`/file-share/projects/${projectIdParam}`, { replace: true });
            }
          }
        } else {
          setSelectedFile(null);
          setCurrentView('files');
        }
      } catch (err) {
        console.error('Failed to load project from URL:', err);
        if (!cancelled) {
          setRouteError('Project not found or could not be loaded.');
          setSelectedProject(null);
          setCurrentView('projects');
          navigate('/file-share', { replace: true });
        }
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    };

    syncFromUrl();
    return () => {
      cancelled = true;
    };
    // Intentionally depend on URL params only — navigation handlers update the URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdParam, fileIdParam, navigate]);

  // Storage-only files are previewed from memory on the project URL (?preview=1).
  // Dropping that flag (breadcrumb, Back to Files, browser back) must close the viewer,
  // otherwise the viewer stays mounted with no file and the page looks empty.
  useEffect(() => {
    if (fileIdParam || previewParam) return;
    setSelectedFile(null);
    setCurrentView((prev) => (prev === 'viewer' ? 'files' : prev));
  }, [previewParam, fileIdParam]);

  const handleProjectSelect = (project) => {
    const projectId = project?.projectId || project?.id;
    if (!projectId) return;
    setSelectedFolder(null);
    setSelectedFile(null);
    setFolderPath([]);
    navigate(`/file-share/projects/${projectId}`);
  };

  /**
   * Build breadcrumb from real parent chain (not click history).
   * Walks parentFolderId up to project root so siblings don't nest wrongly.
   */
  const buildFolderBreadcrumbPath = async (folder) => {
    const { foldersApi } = await import('../../services/fileShareApi');
    const crumbs = [];
    let current = folder;
    const seen = new Set();

    while (current?.folderId) {
      const id = String(current.folderId);
      if (seen.has(id)) break;
      seen.add(id);

      crumbs.unshift({
        folderId: current.folderId,
        folderName: current.folderName || current.name,
      });

      const parentId =
        current.parentFolderId ||
        current.parentFolder?.folderId ||
        current.parent_folder_id ||
        null;
      if (!parentId) break;

      try {
        const response = await foldersApi.getById(parentId);
        current = response.data;
      } catch (err) {
        console.warn('Could not resolve parent folder for breadcrumb:', parentId, err);
        break;
      }
    }

    return crumbs;
  };

  const handleFolderClick = async (folder) => {
    console.log('🔍 FileShare: handleFolderClick called with:', folder);
    if (!folder || !folder.folderId) {
      // Treat null/invalid as go to project root
      setFolderPath([]);
      setSelectedFolder(null);
      setSelectedFile(null);
      return;
    }

    try {
      let folderDetails = folder;
      // Ensure parentFolderId is present so we can walk the real hierarchy
      if (
        folder.parentFolderId === undefined &&
        !folder.parentFolder?.folderId
      ) {
        const { foldersApi } = await import('../../services/fileShareApi');
        const response = await foldersApi.getById(folder.folderId);
        folderDetails = { ...folder, ...(response.data || {}) };
      }

      const path = await buildFolderBreadcrumbPath(folderDetails);
      console.log('🔍 FileShare: Rebuilt folderPath from parents:', path);
      setFolderPath(path);
      setSelectedFolder(folderDetails);
      setSelectedFile(null);
    } catch (err) {
      console.error('Error resolving folder breadcrumb path:', err);
      setFolderPath([
        {
          folderId: folder.folderId,
          folderName: folder.folderName || folder.name,
        },
      ]);
      setSelectedFolder(folder);
      setSelectedFile(null);
    }
  };

  const handleFolderBack = async (targetFolder) => {
    if (!targetFolder) {
      // Go back to root
      setFolderPath([]);
      setSelectedFolder(null);
    } else {
      // Go back to specific folder in path
      const targetIndex = folderPath.findIndex(f => f.folderId === targetFolder.folderId);
      if (targetIndex >= 0) {
        setFolderPath(folderPath.slice(0, targetIndex + 1));
        // Fetch folder details to ensure we have the full object
        try {
          const { foldersApi } = await import('../../services/fileShareApi');
          const response = await foldersApi.getById(targetFolder.folderId);
          setSelectedFolder(response.data);
        } catch (err) {
          console.error('Error fetching folder:', err);
          setSelectedFolder(targetFolder);
        }
      }
    }
    setSelectedFile(null);
  };

  const handleFileSelect = (file) => {
    const fileId = file?.fileId || file?.id;
    if (!selectedProject?.projectId || !fileId || String(fileId).startsWith('storage_')) {
      // Storage-only files have no DB id yet — keep in-memory viewer fallback
      setSelectedFile(file);
      setCurrentView('viewer');
      if (selectedProject?.projectId) {
        navigate(`/file-share/projects/${selectedProject.projectId}?preview=1`);
      }
      return;
    }
    navigate(`/file-share/projects/${selectedProject.projectId}/files/${fileId}`);
  };

  const handleConnectorUpdate = (connectorType, connectorId) => {
    console.log(`🔍 Connector updated: ${connectorType} = ${connectorId}`);
    // Refresh the file list to pick up new connector settings
    setRefreshTrigger(prev => prev + 1);
  };

  const handleBackToFiles = () => {
    if (selectedProject?.projectId) {
      setSelectedFile(null);
      setCurrentView('files');
      navigate(`/file-share/projects/${selectedProject.projectId}`);
    } else {
      navigate('/file-share');
    }
  };

  const handleBackToProjects = (e) => {
    if (e) e.preventDefault();
    setSelectedFile(null);
    setSelectedFolder(null);
    setFolderPath([]);
    setCurrentView('projects');

    const projectIsArchived =
      selectedProject?.archived === true ||
      selectedProject?.isArchived === true ||
      !!selectedProject?.archivedAt;
    let storedView = 'active';
    try {
      storedView = sessionStorage.getItem('fileshare.projects.viewMode') || 'active';
    } catch {
      /* ignore */
    }
    const returnToArchived = projectIsArchived || storedView === 'archived';
    navigate(returnToArchived ? '/file-share?view=archived' : '/file-share');
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleUploadSuccess = (egnytePath) => {
    setShowUploadDialog(false);
    handleRefresh();
    if (egnytePath) {
      setSyncStatus({
        success: true,
        message: `File saved to Egnyte. Path: ${egnytePath}`,
      });
      setTimeout(() => setSyncStatus(null), 8000);
    }
  };

  const handleSyncBucket = async () => {
    if (!selectedProject) return;
    
    setSyncingBucket(true);
    setSyncStatus(null);
    
    try {
      // Sync folders from external storage
      const response = await filesApi.syncProjectFolders(selectedProject.projectId);
      
      const result = response.data;
      setSyncStatus({
        success: result.success,
        created: result.created || 0,
        updated: result.updated || 0,
        deleted: result.deleted || 0,
        errors: result.errors || 0,
        message: result.success 
          ? `Folder sync complete! ${result.created} created, ${result.updated} updated, ${result.deleted} deleted. Refreshing list…`
          : `Sync failed: ${result.message || 'Unknown error'}`
      });
      
      // Refresh folder tree and file list after sync (give backend time to finish DB writes for large projects)
      if (result.success) {
        setTimeout(() => {
          handleRefresh();
          setSyncStatus(null);
          // Hint: if Translate is still disabled, user may need to re-open the folder or refresh the page
        }, 4000);
      } else {
        setTimeout(() => setSyncStatus(null), 5000);
      }
    } catch (error) {
      console.error('Folder sync error:', error);
      const payload = error.response?.data || {};
      setSyncStatus({
        success: false,
        message: payload.message || error.message || 'Failed to sync folders',
      });
      setTimeout(() => setSyncStatus(null), 5000);
    } finally {
      setSyncingBucket(false);
    }
  };

  const getBreadcrumbs = () => {
    const breadcrumbs = [];
    
    // Only show breadcrumbs when we're deeper than the projects level
    if (!selectedProject) {
      return null; // Don't show breadcrumbs on projects page
    }

    const crumbLinkSx = {
      textDecoration: 'none',
      color: 'text.secondary',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      transition: 'color 0.2s ease',
      '&:hover': {
        color: 'primary.main',
        textDecoration: 'underline',
      },
    };

    // Always show projects list as clickable when we're deeper
    breadcrumbs.push(
      <Link
        key="projects"
        component="button"
        type="button"
        onClick={handleBackToProjects}
        sx={crumbLinkSx}
      >
        {selectedProject?.archived || selectedProject?.isArchived || selectedProject?.archivedAt
          ? 'Archived Projects'
          : 'Projects'}
      </Link>
    );

    // Show project as clickable (to go back to project root)
    breadcrumbs.push(
      <Link
        key="project"
        component="button"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          if (selectedProject?.projectId) {
            setSelectedFolder(null);
            setFolderPath([]);
            setSelectedFile(null);
            setCurrentView('files');
            navigate(`/file-share/projects/${selectedProject.projectId}`);
          }
        }}
        sx={crumbLinkSx}
      >
        {selectedProject.projectName}
      </Link>
    );

    // Folder path segments — clickable so user can jump back to that folder
    folderPath.forEach((folder, index) => {
      const isLastFolder = index === folderPath.length - 1;
      const isCurrentLocation = isLastFolder && !selectedFile;

      if (isCurrentLocation) {
        breadcrumbs.push(
          <Typography
            key={`folder-${folder.folderId}`}
            sx={{
              color: 'text.primary',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {folder.folderName}
          </Typography>
        );
      } else {
        breadcrumbs.push(
          <Link
            key={`folder-${folder.folderId}`}
            component="button"
            type="button"
            onClick={async (e) => {
              e.preventDefault();
              if (selectedFile && selectedProject?.projectId) {
                setSelectedFile(null);
                setCurrentView('files');
                navigate(`/file-share/projects/${selectedProject.projectId}`);
              }
              await handleFolderBack(folder);
            }}
            sx={crumbLinkSx}
          >
            {folder.folderName}
          </Link>
        );
      }
    });

    // Fallback if selectedFolder exists but is not in folderPath (edge case)
    if (selectedFolder && !folderPath.some((f) => f.folderId === selectedFolder.folderId)) {
      if (selectedFile) {
        breadcrumbs.push(
          <Link
            key={`folder-${selectedFolder.folderId}`}
            component="button"
            type="button"
            onClick={async (e) => {
              e.preventDefault();
              if (selectedProject?.projectId) {
                setSelectedFile(null);
                setCurrentView('files');
                navigate(`/file-share/projects/${selectedProject.projectId}`);
              }
              await handleFolderBack(selectedFolder);
            }}
            sx={crumbLinkSx}
          >
            {selectedFolder.folderName}
          </Link>
        );
      } else {
        breadcrumbs.push(
          <Typography
            key={`folder-${selectedFolder.folderId}`}
            sx={{
              color: 'text.primary',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {selectedFolder.folderName}
          </Typography>
        );
      }
    }

    if (selectedFile) {
      breadcrumbs.push(
        <Typography
          key="file"
          sx={{
            color: 'text.primary',
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          {selectedFile.fileName || selectedFile.name}
        </Typography>
      );
    }

    return breadcrumbs;
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {routeLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {routeError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRouteError(null)}>
          {routeError}
        </Alert>
      )}

      {/* Header + main content (hidden while loading project from URL) */}
      {!routeLoading && (
      <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {/* Modern Breadcrumbs - Only show when navigating deeper */}
          {getBreadcrumbs() && (
            <Breadcrumbs
              aria-label="breadcrumb"
              sx={{
                '& .MuiBreadcrumbs-separator': {
                  color: 'text.disabled',
                  fontSize: '0.75rem',
                  margin: '0 8px',
                },
                '& .MuiBreadcrumbs-li': {
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiBreadcrumbs-ol': {
                  alignItems: 'center',
                }
              }}
            >
              {getBreadcrumbs()}
            </Breadcrumbs>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {isFilesActive && (
              <Button
                variant="outlined"
                startIcon={<CloudSync />}
                onClick={handleSyncBucket}
                disabled={!selectedProject || syncingBucket}
                color={syncingBucket ? 'secondary' : 'primary'}
                title="Sync folders from external storage"
              >
                {syncingBucket ? 'Syncing Folders...' : 'Sync Folders'}
              </Button>
            )}

            {isViewerActive && (
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleBackToFiles}
              >
                Back to Files
              </Button>
            )}

            {currentView === 'connectors' && (
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleBackToFiles}
              >
                Back to Files
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {(currentView === 'projects' || (isFilesActive && !selectedProject)) && (
          <Box sx={{ height: '100%', minHeight: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            <ProjectSpaces
              onProjectSelect={handleProjectSelect}
              selectedProject={selectedProject}
              refreshTrigger={refreshTrigger}
              onRefresh={handleRefresh}
            />
          </Box>
        )}

        {isFilesActive && selectedProject && (
          <Box sx={{ flex: 1, height: '100%', display: 'flex', gap: 2, minWidth: 0, overflow: 'hidden' }}>
            {/* Folder Tree Sidebar */}
            <SidebarWithCollapse>
              <FolderTree
                projectId={selectedProject.projectId}
                onFolderSelect={handleFolderClick}
                selectedFolder={selectedFolder}
                refreshTrigger={refreshTrigger}
                onFileSelect={handleFileSelect}
                onNavigateHome={() => {
                  handleFolderClick(null);
                }}
              />
            </SidebarWithCollapse>
            
            {/* Main Content Area - Folders and Files */}
            <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
              <FolderContentView
                projectId={selectedProject.projectId}
                currentFolderId={selectedFolder?.folderId}
                currentFolderPath={folderPath}
                onFolderClick={handleFolderClick}
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                refreshTrigger={refreshTrigger}
                onShare={(file) => {
                  setSelectedFileForShare(file);
                  setShareDialogOpen(true);
                }}
                onBack={handleFolderBack}
              />
            </Box>
          </Box>
        )}

        {isViewerActive && (
          <Box sx={{ height: '100%', minHeight: 0, overflow: 'hidden', p: 2, boxSizing: 'border-box' }}>
            <FileViewer
              file={selectedFile}
              onClose={handleBackToFiles}
              onShare={(file) => {
                console.log('🔍 FileShare: onShare called from FileViewer with file:', file);
                setSelectedFileForShare(file);
                setShareDialogOpen(true);
              }}
            />
          </Box>
        )}

        {currentView === 'connectors' && selectedProject && (
          <Box sx={{ p: 3 }}>
            <ProjectConnectors
              projectId={selectedProject.projectId}
              projectName={selectedProject.projectName}
              onConnectorUpdate={handleConnectorUpdate}
            />
          </Box>
        )}
      </Box>
      </>
      )}

      {/* Upload Dialog */}
      <FileUpload
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onSuccess={handleUploadSuccess}
        folderId={selectedFolder?.folderId || null}
        projectId={selectedProject?.projectId}
      />

      {/* Sync Status Snackbar */}
      <Snackbar
        open={syncStatus !== null}
        autoHideDuration={syncStatus?.success ? 3000 : 5000}
        onClose={() => setSyncStatus(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSyncStatus(null)} 
          severity={syncStatus?.success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {syncStatus?.message}
        </Alert>
      </Snackbar>

      {/* Public Share Dialogs */}
      <PublicShareDialog
        open={shareDialogOpen}
        onClose={() => {
          setShareDialogOpen(false);
          setSelectedFileForShare(null);
        }}
        file={selectedFileForShare}
      />

      <PublicShareListDialog
        open={shareListDialogOpen}
        onClose={() => {
          setShareListDialogOpen(false);
          setSelectedFileForShare(null);
        }}
        file={selectedFileForShare}
      />
    </Box>
  );
};

export default FileShare;

